import type { ReactNode } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Num, Plural, T, Var } from 'gt-react';
import type { AgentRunSummary } from '@erp/rpc';
import { cn } from 'src/lib/utils';
import {
  formatRunDuration,
  type OpenTaskPreview,
  type RunStats,
} from '../agentPageRuns';
import { AgentPageOpenTasksStat } from './AgentPageOpenTasksStat';

function StatTile({
  label,
  value,
  sub,
  tone,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'good' | 'bad';
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5 px-4 py-2.5">
      <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-stone-500">
        {label}
      </span>
      <span
        className={cn(
          'truncate text-base font-semibold tabular-nums text-stone-900',
          tone === 'good' && 'text-emerald-700',
          tone === 'bad' && 'text-red-700',
        )}
      >
        {value}
      </span>
      {sub && (
        <span className="truncate text-[11px] text-muted-foreground">
          {sub}
        </span>
      )}
    </div>
  );
}

/**
 * What is wrong with the recent runs, if anything: failures the agent owns,
 * and runs the start gate refused (which are neither a success nor the
 * agent's failure, but a blocked agent is not healthy either).
 */
function RecentRunsSub({
  stats,
}: {
  stats: Pick<RunStats, 'failed' | 'notStarted'>;
}) {
  if (stats.failed > 0 && stats.notStarted > 0) {
    return (
      <T context="agent page stat sublabel, failed run count and count of runs the start gate refused">
        <Num>{stats.failed}</Num> failed · <Num>{stats.notStarted}</Num> did not
        start
      </T>
    );
  }
  if (stats.failed > 0) {
    return (
      <T context="agent page stat sublabel, failed run count">
        <Num>{stats.failed}</Num> failed
      </T>
    );
  }
  if (stats.notStarted > 0) {
    return (
      <T context="agent page stat sublabel, count of runs the start gate refused">
        <Num>{stats.notStarted}</Num> did not start
      </T>
    );
  }
  return (
    <T context="agent page stat sublabel: the recent-runs count leaves out runs that did not start because the agent was only waiting">
      not counting skipped runs
    </T>
  );
}

/**
 * Under "Last run": how long it took (or that it is still going), and — when
 * a skipped check is newer than that run — when the agent last checked for
 * work, so a quiet agent does not look dead.
 */
function lastRunSub(
  latestRun: AgentRunSummary | null,
  latestAttemptAt: string | null,
): ReactNode {
  const ran = lastRunTiming(latestRun);
  const checkedAt = newerCheck(latestRun, latestAttemptAt);
  if (!checkedAt) return ran;
  const checkedAgo = formatDistanceToNow(checkedAt, { addSuffix: true });
  if (!ran) {
    return (
      <T context="agent page stat sublabel: when the agent last checked whether it had work (that check did not start a run)">
        checked <Var>{checkedAgo}</Var>
      </T>
    );
  }
  return (
    <>
      {ran} ·{' '}
      <T context="agent page stat sublabel: when the agent last checked whether it had work (that check did not start a run)">
        checked <Var>{checkedAgo}</Var>
      </T>
    </>
  );
}

function lastRunTiming(latestRun: AgentRunSummary | null): ReactNode {
  if (!latestRun) return null;
  const duration = formatRunDuration(latestRun.duration);
  if (duration) return duration;
  if (latestRun.status !== 'running') return null;
  return (
    <T context="agent page stat sublabel while a run is live">in progress</T>
  );
}

/** The latest attempt's time when it is newer than the latest real run. */
function newerCheck(
  latestRun: AgentRunSummary | null,
  latestAttemptAt: string | null,
): Date | null {
  if (!latestAttemptAt) return null;
  const attempt = new Date(latestAttemptAt);
  if (latestRun && attempt <= new Date(latestRun.startedAt)) return null;
  return attempt;
}

/**
 * At-a-glance strip: when the agent last ran and how it went, how busy it has
 * been this week, how reliable it is, and how much work is waiting for it.
 */
export function AgentPageStats({
  latestRun,
  latestAttemptAt,
  stats,
  openTasks,
  onOpenTasksClick,
}: {
  /** The newest run that did (or is doing) work; skipped checks never count. */
  latestRun: AgentRunSummary | null;
  /** When the agent last tried to run, skipped checks included. */
  latestAttemptAt: string | null;
  stats: RunStats;
  /** Open tasks assigned to the agent; null when the agent has no task queue. */
  openTasks: OpenTaskPreview | null;
  onOpenTasksClick?: () => void;
}) {
  const successPct =
    stats.successRate === null ? null : Math.round(stats.successRate * 100);
  const averageDuration = formatRunDuration(stats.averageDurationMs);

  return (
    <div className="flex divide-x divide-border overflow-hidden rounded-lg border border-border bg-white">
      <StatTile
        label={<T context="agent page stat label">Last run</T>}
        value={
          latestRun ? (
            formatDistanceToNow(new Date(latestRun.startedAt), {
              addSuffix: true,
            })
          ) : (
            <T context="agent page stat value when the agent never ran">
              Never
            </T>
          )
        }
        sub={lastRunSub(latestRun, latestAttemptAt)}
      />
      <StatTile
        label={<T context="agent page stat label">Recent runs</T>}
        value={<Num>{stats.total}</Num>}
        sub={<RecentRunsSub stats={stats} />}
      />
      <StatTile
        label={<T context="agent page stat label">Success rate</T>}
        value={successPct === null ? '—' : `${successPct}%`}
        tone={
          successPct === null ? undefined : successPct >= 90 ? 'good' : 'bad'
        }
        sub={
          stats.successRateSample > 0 ? (
            averageDuration ? (
              <T context="agent page stat sublabel, success rate and average duration over the newest runs">
                last <Num>{stats.successRateSample}</Num>{' '}
                <Plural n={stats.successRateSample} one="run" other="runs" /> ·
                avg. <Var>{averageDuration}</Var>
              </T>
            ) : (
              <T context="agent page stat sublabel, success rate over the newest runs">
                last <Num>{stats.successRateSample}</Num>{' '}
                <Plural n={stats.successRateSample} one="run" other="runs" />
              </T>
            )
          ) : undefined
        }
      />
      {openTasks !== null && (
        <AgentPageOpenTasksStat
          preview={openTasks}
          onOpenTasksClick={onOpenTasksClick}
        />
      )}
    </div>
  );
}
